import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type UseCoverTabsToAnchorParams = {
  /**
   * true일 때만 오프셋을 계산해서 container가 anchor까지 "덮도록" 만듭니다.
   * (예: 카드가 닫힌 상태에서만 enabled=true)
   */
  enabled: boolean;
};

export function useCoverTabsToAnchor({ enabled }: UseCoverTabsToAnchorParams) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [coverOffsetPx, setCoverOffsetPx] = useState(0);

  const recomputeCoverOffset = useCallback(() => {
    if (!enabled) return;
    const containerEl = containerRef.current;
    const anchorEl = anchorRef.current;
    if (!containerEl || !anchorEl) return;

    const containerRect = containerEl.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();
    const marginTopStr = window.getComputedStyle(containerEl).marginTop;
    const appliedMarginTop = Number.parseFloat(marginTopStr || '0') || 0;

    // marginTop 적용 전(기본 위치)의 container top 복원
    const baselineContainerTop = containerRect.top - appliedMarginTop;
    const nextOffset = Math.max(0, Math.round(baselineContainerTop - anchorRect.top));

    setCoverOffsetPx((prev) => {
      // 1px 이내 흔들림은 무시(서브픽셀/리플로우로 인한 반복 업데이트 방지)
      if (Math.abs(prev - nextOffset) <= 1) return prev;
      return nextOffset;
    });
  }, [enabled]);

  useLayoutEffect(() => {
    recomputeCoverOffset();
  }, [recomputeCoverOffset]);

  useEffect(() => {
    if (!enabled) return;
    const onResize = () => recomputeCoverOffset();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, recomputeCoverOffset]);

  const containerStyle = enabled ? { marginTop: -coverOffsetPx } : { marginTop: 0 };

  return {
    containerRef,
    anchorRef,
    containerStyle,
    coverOffsetPx,
    recomputeCoverOffset
  };
}
